import { Alert } from '@cmsgov/ds-medicare-gov';
import * as PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { allow } from '../../common/classes/allow';
import Button from '../../common/components/button';
import DateFields from '../../common/components/date.fields';
import Div from '../../common/components/div';
import { getChatUrl } from '../../common/functions/get.chat.url';
import H1 from '../../common/components/h1';
import InputField from '../../common/components/input.field';
import MDAndLargerDiv from '../../common/components/md.and.larger.div';
import MedicareNumberDialog from '../../common/components/medicare.number.dialog';
import MedicareNumberLabel from '../../common/components/medicare.number.label';
import StackingButtons from '../../common/components/stacking.buttons';
import StepHeader from '../../common/components/step.header';
import SubHeading from '../../common/components/sub.heading.section';
import SuffixDropdown from '../../common/components/suffix.dropdown';
import TranslatedSpan from '../../common/components/translated.span';
import ValidationAlertMessage from '../../common/components/validation.alert.message';
import ValidationAlerts from '../../common/components/validation.alerts';
import XSAndSMDiv from '../../common/components/xs.and.sm.div';
import capitalizeFirstLetter from '../../common/functions/capitalize.first.letter';
import capitalizeWords from '../../common/functions/capitalize.words';
import { isValidMedicareNumber } from '../../common/functions/is.valid.medicare.number';
import { isValidYear } from '../../common/functions/is.valid.year';
import translate from '../../common/functions/translate';
import { useConversion, useFormStart, usePageViewWithSteps } from '../../common/hooks/use.analytics';
import { usePageTitle } from '../../common/hooks/use.page.title';
import useTrait from '../../common/hooks/use.trait';
import dateFieldValue from '../../common/objects/models/date.field.value';
import use from '../../common/objects/use';
import NoAccountInRecords from '../../find-my-account/components/no.account.in.records';

const ForgotUsername = props => {
    const initData = props.initData || {};
   
    const [dayRef, setDayRef] = useState();
    const [lastNameRef, setLastNameRef] = useState();
    const [medicareNumberRef, setMedicareNumberRef] = useState();
    const [monthRef, setMonthRef] = useState();
    const [yearRef, setYearRef] = useState();
    const dateOfBirth = useTrait(initData.dateOfBirth || dateFieldValue);
    const dialogIsOpen = useTrait(false);
    const history = useHistory();
    const lastName = useTrait(initData.lastName || '');
    const lastNameError = useTrait(false);
    const medicareNumber = useTrait(initData.medicareNumber || '');
    const medicareNumberError = useTrait(false);
    const mostRecentUnhandledErrorCode = useTrait(null);
    const suffix = useTrait(initData.suffix || '');
    const username = useTrait(initData.username || '');
    const validateEntireForm = useTrait(false);
    const apiMessage = useTrait(null);
    const [isLoading, setIsLoading] = useState(false);
   
    const day = translate('day');
    const month = translate('month');
    const year = translate('year');
   
    const formStart = useFormStart('username_reminder');
    const analyticsComponentName = props.combinedWithPassword ? 'forgot username and password' : 'forgot username';
    const conversion = useConversion(analyticsComponentName, 'username_reminder');
    usePageTitle(props.titleText || translate('Get username'));
    const isIntermediateConversionAttempt = props.combinedWithPassword;

    const callVerifyUser = async () => {
        const lastNameWithSuffix = suffix.get() ? `${lastName.get()} ${suffix.get()}` : lastName.get();
        const response = await use.verifyAccountApi.post(medicareNumber.get(), lastNameWithSuffix, dateOfBirth.get());
        setIsLoading(false);
   
        const conversionFail = isIntermediateConversionAttempt ? conversion.failAPIIntermediate : conversion.failAPI;
   
        if (!response?.data) {
            mostRecentUnhandledErrorCode.set(null);
            username.set('');
            conversionFail('unknown error');
            return;
        }
   
        mostRecentUnhandledErrorCode.set(response.data.code ? response.data.code : null);
        username.set(response.data.username ? response.data.username : null);
   
        let errorHandled = false;
        let errors = response.data?.errors;
        let analyticsMessage;
        switch (response.data.code) {
                case 400:
                    let message;
                    const isLocked = errors.some(x => x.message.includes('locked'));
                    const isDisabled = errors.some(x => x.message.includes('disabled'));
                    if (isLocked) {
                        errorHandled = true;
                        message = <>
                            {translate('Your account is now locked to protect your information.')}
                            {' '}<Link to="/unlock-account">{translate('Unlock My Account')}.</Link>
                        </>;
                        analyticsMessage = `${translate('Error')}: ${translate('Your account is now locked to protect your information.')} ${translate('Unlock My Account')}`
                    } else if (isDisabled){
                        errorHandled = true;
                        message = <>
                            {translate('Your account is no longer active. To re-open your account,')}
                            <a href={getChatUrl()}>{translate('chat with us')}</a> {translate('or call 1-800-MEDICARE (1-800-633-4227). TTY users can call 1-877-486-2048.')}
                        </>;
                        analyticsMessage = `${translate('Error')}: ${translate('Your account is no longer active. To re-open your account,')}
                  ${translate('chat with us')} ${translate('or call 1-800-MEDICARE (1-800-633-4227). TTY users can call 1-877-486-2048.')}`
                    } else {
                        errorHandled = true;
                        message = translate('We’re not able to find a matching record for the information you provided. Please check the information and try again.');
                    }
                    apiMessage.set(message);
                    break;
                case 403:
                    if (errors.some(x => x.message.includes('too many incorrect'))) {
                        errorHandled = true;
                        apiMessage.set('Your account was locked to protect your information. You can try again in 24 hours.');
                    }
                    break;
                default:
                    break;
        }
   
        if (errorHandled) {
            mostRecentUnhandledErrorCode.set(null);
        }
   
        if (username.get()) {
            if (!isIntermediateConversionAttempt) {
                conversion.success();
            }
        } else {
            const errorMessage = analyticsMessage ? analyticsMessage : apiMessage.get() || 'unknown error';
            conversion.failAPIIntermediate(errorMessage);
            conversion.failAPI(errorMessage);
        }

        // for forgot username and password flow - pass data on to reset password
        if (username.get() && props.onData) {
            props.onData({
                lastName: lastName.get(),
                suffix: suffix.get(),
                dateOfBirth: dateOfBirth.get(),
                medicareNumber: medicareNumber.get(),
                username: username.get(),
            });
        }
    };
   
    const getBackButton = () => {
        return <>
            <Button
                color={'teal'}
                displayAs={'button'}
                fill={'outlined'}
                size={'small'}
                onClick={() => username.set('')}
            >
                <TranslatedSpan english={'Back'}/>
            </Button>
        </>;
    };
   
    const getCancelButton = () => {
        return <>
            <Button
                color={'teal'}
                displayAs={'button'}
                fill={'outlined'}
                onClick={() => history.push('/login')}
                size={'small'}
            >
                <TranslatedSpan english={'Cancel'}/>
            </Button>
        </>;
    };
   
    const getGetUsernameButton = () => {
        return <>
            <Button
                color={'teal'}
                displayAs={'button'}
                fill={'solid'}
                onClick={handleGetUsernameButtonClick}
                size={'small'}
                isLoading={isLoading}
            >
                {props.buttonText || capitalizeWords(translate('Get username'))}
            </Button>
        </>;
    };
   
    const getLastNameInputField = () => {
        return <>
            <InputField
                aria-invalid={lastNameError.get().toString()}
                errorMessage={lastNameError.get() ? `${translate('Error')}: ${translate('Enter your last name.')}` : null}
                inputRef={setLastNameRef}
                label={translate('Last name')}
                name={'lastName'}
                onClick={formStart}
                onFocus={formStart}
                onBlur={event => updateLastName(event.currentTarget.value)}
                onChange={event => updateLastName(event.currentTarget.value)}
                style={{minWidth: '100%'}}
                value={lastName.get()}
            />
        </>;
    };
   
    const getNextButton = () => {
        return <>
            <Button
                color={'teal'}
                displayAs={'button'}
                fill={'solid'}
                size={'small'}
                onClick={props.onNextClick}
                isLoading={props.onNextLoading}
            >
                <TranslatedSpan english={'Next'}/>
            </Button>
        </>;
    };
   
    const getSuffixDropdown = () => {
        return <>
            <SuffixDropdown
                onClick={formStart}
                onChange={event => suffix.set(event.currentTarget.value)}
                style={{
                    paddingLeft: 8,
                    paddingRight: 28,
                }}
                value={suffix.get()}
            />
        </>;
    };
   
    const getValidationAlertMessages = () => {
        const messages = [];
        if (!validateEntireForm.get())
            return messages;
        if (!validateMedicareNumber())
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter your Medicare Number.')}
                    fieldReference={medicareNumberRef}
                    linkText={translate('Medicare Number')}
                />
            );
        if (!validateLastName())
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter your last name.')}
                    fieldReference={lastNameRef}
                    linkText={translate('Last name')}
                />
            );
        if (dateOfBirth.get().month === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter the {{timePeriod}} you were born.', {timePeriod: month})}
                    fieldReference={monthRef}
                    linkText={capitalizeFirstLetter(month)}
                />
            );
        if (dateOfBirth.get().day === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter the {{timePeriod}} you were born.', {timePeriod: day})}
                    fieldReference={dayRef}
                    linkText={capitalizeFirstLetter(day)}
                />
            );
        if (!isValidYear(dateOfBirth.get().year))
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter the {{timePeriod}} you were born.', {timePeriod: year})}
                    fieldReference={yearRef}
                    linkText={capitalizeFirstLetter(year)}
                />
            );
        return messages;
    };
   
    const getYourUsernameButtons = () => {
        if (props.combinedWithPassword)
            return <>
                <StackingButtons
                    button1={getBackButton()}
                    button2={getNextButton()}
                />
            </>;
        else
            return <>
                <Div isRow={true} paddingBottom={3} paddingTop={2} style={{paddingLeft: 16}}>
                    <Button
                        color={'teal'}
                        displayAs={'button'}
                        fill={'solid'}
                        size={'small'}
                        onClick={() => history.push('/login')}
                    >
                        <TranslatedSpan english={'Back to Login'}/>
                    </Button>
                </Div>
            </>;
    };
   
    const handleGetUsernameButtonClick = () => {
        if (isLoading) {
            return;
        }
        setIsLoading(true);
   
        conversion.attempt();
        const errorFieldRef = validateForm();
        if (errorFieldRef !== null) {
            errorFieldRef.focus();
            setIsLoading(false);
            return;
        }
        callVerifyUser();
    };
   
    const updateDateOfBirth = (dateObject = {}) => {
        allow.anInstanceOf(dateObject, dateFieldValue);
        formStart();
        dateOfBirth.set(dateObject);
        if (validateEntireForm.get())
            validateForm();
    };
   
    const updateLastName = (value = '') => {
        allow.aString(value);
        formStart();
        const newValue = value.trimStart();
        lastName.set(newValue);
        validateLastName();
        if (validateEntireForm.get())
            validateForm();
    };
   
    const updateMedicareNumber = (value = '') => {
        allow.aString(value);
        formStart();
        const newValue = value.trim();
        medicareNumber.set(newValue);
        validateMedicareNumber();
        if (validateEntireForm.get())
            validateForm();
    };
   
    const validateForm = () => {
        if (!validateEntireForm.get())
            validateEntireForm.set(true);
        let errorFieldRef = null;
        if (!validateMedicareNumber())
            errorFieldRef = medicareNumberRef;
        if (!validateLastName())
            errorFieldRef = lastNameRef;
        if (dateOfBirth.get().month === '')
            errorFieldRef = monthRef;
        if (dateOfBirth.get().day === '')
            errorFieldRef = dayRef;
        if (!isValidYear(dateOfBirth.get().year))
            errorFieldRef = yearRef;
        const messages = getValidationAlertMessages();
        if (messages.length) {
            const errorString = messages.map(x => x.props.description).join(', ');
            if (isIntermediateConversionAttempt) {
                conversion.failValidationIntermediate(errorString);
            } else {
                conversion.failValidation(errorString);
            }
        }
        return messages.length ? errorFieldRef : null;
    };
   
    const validateLastName = () => {
        const isValid = lastName.get() !== '';
        if (lastNameError.get() === isValid)
            lastNameError.set(!isValid);
        return isValid;
    };
   
    const validateMedicareNumber = () => {
        const isValid = isValidMedicareNumber(medicareNumber.get());
        if (medicareNumberError.get() === isValid)
            medicareNumberError.set(!isValid);
        return isValid;
    };
   
    const enterYourInfo = (
        <>
            <MedicareNumberDialog
                isOpen={dialogIsOpen.get()}
                onExit={() => dialogIsOpen.set(false)}
            />
            <Div
                isContainer={true}
                paddingySM={1} paddingyMD={2} paddingyLG={3} paddingyXL={4}
            >
                <fieldset style={{
                    border: 0,
                    margin: 0,
                    padding: 0,
                }}>
                    <Div isRow={true}>
                        <Div cols={12}>
                            <H1>{props.titleText || translate('Get username')}</H1>
                            {props.subTitle &&
                     <Div paddingBottom={4}>
                         <div>{props.subTitle}</div>
                     </Div>
                            }
                            {props.combinedWithPassword &&
                     <Div paddingBottom={3}>
                         <StepHeader
                             currentStep={1}
                             numSteps={4}
                             stepTitle={translate('Enter your information')}
                         />
                     </Div>
                            }
                            <SubHeading style={{fontSize: 14, fontWeight: 400}}>
                                <TranslatedSpan english={'All fields required.'}/>
                            </SubHeading>
                            <ValidationAlerts
                                heading={`${translate('Error')}: ${translate('Complete these items to get your username.')}`}
                                validationMessages={getValidationAlertMessages()}
                            />
                            {apiMessage.get() &&
                     <Alert
                         role={'alert'}
                         variation={'error'}
                     >
                         {translate('Error')}: {apiMessage.get()}
                     </Alert>
                            }
                        </Div>
                    </Div>
                    <Div isRow={true}>
                        <Div
                            cols={12} colsSM={12} colsMD={8} colsLG={6} colsXL={5}
                            paddingRightLG={0}
                        >
                            <InputField
                                aria-invalid={medicareNumberError.get().toString()}
                                errorMessage={medicareNumberError.get() ? `${translate('Error')}: ${translate('Enter your Medicare Number.')}` : null}
                                inputRef={setMedicareNumberRef}
                                label={<MedicareNumberLabel onClick={() => dialogIsOpen.set(true)}/>}
                                name={'medicareNumber'}
                                onClick={formStart}
                                onFocus={formStart}
                                onBlur={event => updateMedicareNumber(event.currentTarget.value)}
                                onChange={event => updateMedicareNumber(event.currentTarget.value)}
                                style={{minWidth: '100%'}}
                                value={medicareNumber.get()}
                            />
                        </Div>
                    </Div>
                    <XSAndSMDiv>
                        <Div isRow={true}>
                            <Div cols={12}>
                                {getLastNameInputField()}
                            </Div>
                        </Div>
                        <Div isRow={true}>
                            <Div cols={8}>
                                {getSuffixDropdown()}
                            </Div>
                        </Div>
                    </XSAndSMDiv>
                    <MDAndLargerDiv>
                        <Div isRow={true}>
                            <Div
                                colsMD={6} colsLG={5} colsXL={4}
                                paddingRight={1}
                            >
                                {getLastNameInputField()}
                            </Div>
                            <Div
                                cols={2} colsLG={1}
                                paddingLeft={0} paddingRightLG={0}
                            >
                                {getSuffixDropdown()}
                            </Div>
                        </Div>
                    </MDAndLargerDiv>
                    <Div isRow={true}>
                        <Div cols={12}>
                            <DateFields
                                date={dateOfBirth.get()}
                                dayErrorMessage={`${translate('Error')}: ${translate('Enter the {{timePeriod}} you were born.', {timePeriod: day})}`}
                                dayFieldRef={setDayRef}
                                forceValidation={validateEntireForm.get()}
                                hint={translate('Use the format MM/DD/YYYY')}
                                label={translate('Date of Birth')}
                                monthErrorMessage={`${translate('Error')}: ${translate('Enter the {{timePeriod}} you were born.', {timePeriod: month})}`}
                                monthFieldRef={setMonthRef}
                                onBlur={date => updateDateOfBirth(date)}
                                onChange={date => updateDateOfBirth(date)}
                                yearErrorMessage={`${translate('Error')}: ${translate('Enter the {{timePeriod}} you were born.', {timePeriod: year})}`}
                                yearFieldRef={setYearRef}
                            />
                        </Div>
                    </Div>
                </fieldset>
                <StackingButtons
                    button1={getCancelButton()}
                    button2={getGetUsernameButton()}
                />
            </Div>
        </>
    );
   
    const yourUsername = (
        <Div isContainer={true}>
            <Div isRow={true}>
                <Div cols={12}>
                    <H1>{props.combinedWithPassword ? translate('Forgot username and password') : translate('Your username')}</H1>
                </Div>
            </Div>
            {props.subTitle &&
         <div>{props.subTitle}</div>
            }
            {props.combinedWithPassword &&
         <StepHeader
             currentStep={2}
             numSteps={4}
             stepTitle={translate('Username reminder')}
         />
            }
            {props.getSecretQuestionErrorMessage &&
         <Alert
             role={'alert'}
             variation={'error'}
         >
             {props.getSecretQuestionErrorMessage}
         </Alert>
            }
            <div
                style={{marginBottom: 30, marginTop: 30}}
            >
                <TranslatedSpan english={'Your username is'}/> <span style={{fontWeight: 'bold'}}>{username.get()}</span>.
            </div>
            {getYourUsernameButtons()}
        </Div>
    );
   
    const hasUsername = Boolean(username.get());
    const stepNum = hasUsername ? 2 : 1;
    const stepTitle = hasUsername ? 'Username reminder' : 'Enter your information';
    usePageViewWithSteps(props.titleText || translate('Get username'), stepNum, stepTitle);
   
    if (mostRecentUnhandledErrorCode.get() === 404){
        conversion.failAPIIntermediate(translate('No account in our records'));
        conversion.failAPI(translate('No account in our records'));
        return <NoAccountInRecords/>;
    }
    else if (hasUsername)
        return yourUsername;
    else
        return enterYourInfo;
};

ForgotUsername.propTypes = {
    titleText: PropTypes.string,
    subTitle: PropTypes.string,
    combinedWithPassword: PropTypes.bool,
    onData: PropTypes.func,
    onNextClick: PropTypes.func,
    onNextLoading: PropTypes.bool, // for forgot username and password
    initData: PropTypes.object,
    getSecretQuestionErrorMessage: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element
    ])
};

export default ForgotUsername;